import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Link2,
  ExternalLink,
  Trash2,
  Search,
  Filter,
  Globe,
  User,
  CheckCircle2,
  XCircle,
  BarChart3,
  ShieldAlert,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatRelativeTime, cn } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Input } from '@/client/components/ui/input';
import { Badge } from '@/client/components/ui/badge';
import { Skeleton } from '@/client/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';

type LinkType = 'all' | 'public' | 'user';
type StatusFilter = 'all' | 'active' | 'inactive' | 'malicious';

interface AdminLink {
  id: string;
  shortCode: string;
  shortUrl: string;
  destinationUrl: string;
  title: string | null;
  isActive: boolean;
  isPublic: boolean;
  isMalicious: boolean;
  maliciousThreatType: string | null;
  lastSafetyCheck: string | null;
  totalClicks: number;
  validUntil: string | null;
  createdAt: string;
  user: {
    id: string;
    name: string;
    email: string;
  } | null;
}

export function AdminLinksPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [type, setType] = useState<LinkType>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'links', page, type, statusFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        type,
      });
      if (search) params.set('search', search);
      if (statusFilter === 'active') params.set('isActive', 'true');
      if (statusFilter === 'inactive') params.set('isActive', 'false');
      if (statusFilter === 'malicious') params.set('isMalicious', 'true');

      const response = await apiFetch(`/api/admin/links?${params}`);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Admin links API error:', response.status, errorText);
        throw new Error(`Failed to fetch links: ${response.status}`);
      }
      const json = await response.json();
      console.log('Admin links API response:', json);
      return json;
    },
  });

  // Debug log
  console.log('Admin links state:', { data, isLoading, error });

  const deleteMutation = useMutation({
    mutationFn: async (linkId: string) => {
      const response = await apiFetch(`/api/admin/links/${linkId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete link');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'links'] });
      toast.success('Link deleted');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const truncateUrl = (url: string, maxLength = 50) => {
    if (url.length <= maxLength) return url;
    return `${url.substring(0, maxLength - 3)}…`;
  };

  return (
    <div className="space-y-6 stagger-children">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">All Links</h1>
        <p className="text-muted-foreground mt-1">View and manage all links across the platform.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by short code, URL, or title…"
              className="pl-10 rounded-xl"
            />
          </div>
          <Button type="submit" className="rounded-xl">
            Search
          </Button>
        </form>

        <div className="flex gap-2">
          <Button
            variant={type === 'all' ? 'default' : 'secondary'}
            onClick={() => {
              setType('all');
              setPage(1);
            }}
            className="rounded-xl"
          >
            <Filter className="size-4" />
            All
          </Button>
          <Button
            variant={type === 'public' ? 'default' : 'secondary'}
            onClick={() => {
              setType('public');
              setPage(1);
            }}
            className="rounded-xl"
          >
            <Globe className="size-4" />
            Public
          </Button>
          <Button
            variant={type === 'user' ? 'default' : 'secondary'}
            onClick={() => {
              setType('user');
              setPage(1);
            }}
            className="rounded-xl"
          >
            <User className="size-4" />
            User
          </Button>
        </div>
      </div>

      {/* Status Filter */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Status:</span>
        <Button
          variant={statusFilter === 'all' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => {
            setStatusFilter('all');
            setPage(1);
          }}
          className="rounded-lg"
        >
          All
        </Button>
        <Button
          variant={statusFilter === 'active' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => {
            setStatusFilter('active');
            setPage(1);
          }}
          className={cn(
            'rounded-lg',
            statusFilter === 'active' && 'bg-success/10 text-success hover:bg-success/20'
          )}
        >
          <CheckCircle2 className="size-3.5" />
          Active
        </Button>
        <Button
          variant={statusFilter === 'inactive' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => {
            setStatusFilter('inactive');
            setPage(1);
          }}
          className={cn(
            'rounded-lg',
            statusFilter === 'inactive' &&
              'bg-destructive/10 text-destructive hover:bg-destructive/20'
          )}
        >
          <XCircle className="size-3.5" />
          Inactive
        </Button>
        <Button
          variant={statusFilter === 'malicious' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => {
            setStatusFilter('malicious');
            setPage(1);
          }}
          className={cn(
            'rounded-lg',
            statusFilter === 'malicious' &&
              'bg-orange-500/10 text-orange-600 hover:bg-orange-500/20'
          )}
        >
          <ShieldAlert className="size-3.5" />
          Malicious
        </Button>
      </div>

      {/* Links Table */}
      <Card className="rounded-2xl overflow-hidden py-0">
        {error ? (
          <CardContent className="p-12 text-center">
            <div className="size-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <Link2 className="size-8 text-destructive/50" />
            </div>
            <h3 className="text-lg font-semibold text-destructive mb-2">Error Loading Links</h3>
            <p className="text-muted-foreground">{error.message}</p>
            <p className="text-sm text-muted-foreground mt-2">
              Check the browser console for more details.
            </p>
          </CardContent>
        ) : isLoading ? (
          <CardContent className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 rounded-xl" />
            ))}
          </CardContent>
        ) : data?.links?.length === 0 ? (
          <CardContent className="p-12 text-center">
            <div className="size-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
              <Link2 className="size-8 text-muted-foreground/50" />
            </div>
            <p className="text-muted-foreground">No links found.</p>
          </CardContent>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Short Link</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Clicks</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.links?.map((link: AdminLink) => (
                    <TableRow key={link.id} className={cn(!link.isActive && 'opacity-60')}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <a
                            href={link.shortUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            {link.shortUrl.replace('https://', '')}
                            <ExternalLink className="size-3" />
                          </a>
                          {link.isPublic && (
                            <Badge variant="primary-subtle" className="text-xs">
                              Public
                            </Badge>
                          )}
                        </div>
                        {link.title && (
                          <div className="text-xs text-muted-foreground mt-0.5">{link.title}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <a
                          href={link.destinationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-muted-foreground hover:text-foreground"
                          title={link.destinationUrl}
                        >
                          {truncateUrl(link.destinationUrl)}
                        </a>
                      </TableCell>
                      <TableCell>
                        {link.user ? (
                          <Link
                            to={`/admin/users/${link.user.id}`}
                            className="text-sm hover:underline hover:text-primary"
                          >
                            <div className="font-medium">{link.user.name}</div>
                            <div className="text-muted-foreground text-xs">{link.user.email}</div>
                          </Link>
                        ) : (
                          <span className="text-muted-foreground text-sm">Anonymous</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {link.isMalicious ? (
                          <Badge variant="destructive" className="gap-1">
                            <ShieldAlert className="size-3" />
                            Malicious
                          </Badge>
                        ) : link.isActive ? (
                          <Badge variant="success" className="gap-1">
                            <span className="size-1.5 rounded-full bg-current" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1">
                            <span className="size-1.5 rounded-full bg-current" />
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold">{link.totalClicks.toLocaleString()}</span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatRelativeTime(link.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {link.user && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  asChild
                                  className="rounded-lg"
                                >
                                  <Link to={`/links/${link.shortCode}`}>
                                    <BarChart3 className="size-4" />
                                  </Link>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View analytics</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => {
                                  if (confirm('Are you sure you want to delete this link?')) {
                                    deleteMutation.mutate(link.id);
                                  }
                                }}
                                disabled={deleteMutation.isPending}
                                className="rounded-lg hover:bg-destructive/10 hover:text-destructive"
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete link</TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {data?.pagination && data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30">
                <div className="text-sm text-muted-foreground">
                  Showing {(page - 1) * 20 + 1} - {Math.min(page * 20, data.pagination.total)} of{' '}
                  {data.pagination.total} links
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-lg"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
                    disabled={page === data.pagination.totalPages}
                    className="rounded-lg"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
